/* Storefront filtering (§11.2).
   <facet-filters>  checkbox / sort changes (and debounced price input) → fetch the section with the
                    new query → swap [data-facets-part] regions → history.replaceState. Pills and
                    "Clear all" are links handled the same way. Keeps closed groups closed and focus
                    in place; announces the new count. Grid toggle (3 / 4) is remembered.
   <load-more>      replaces numbered pagination with a button that appends the next page. */
(() => {
  if (customElements.get('facet-filters')) return;
  const COLS_KEY = 'theme:grid-cols';

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.debouncedApply = window.Theme.debounce(() => this.apply(this.formParams()), 500);
      this.onChange = (event) => {
        if (event.target.type === 'number') return;
        if (event.target.closest('[data-facets-form]') || event.target.name === 'sort_by') this.apply(this.formParams());
      };
      this.onInput = (event) => {
        if (event.target.type === 'number' && event.target.closest('[data-facets-form]')) this.debouncedApply();
      };
      this.onSubmit = (event) => {
        if (!event.target.matches('[data-facets-form]')) return;
        event.preventDefault();
        this.apply(this.formParams());
      };
      this.onClick = (event) => {
        const link = event.target.closest('[data-facet-link]');
        const cols = event.target.closest('[data-grid-cols]');
        if (link) {
          event.preventDefault();
          this.apply(new URL(link.href, window.location.origin).searchParams);
        } else if (cols) {
          this.setCols(cols.dataset.gridCols, true);
        }
      };
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.restoreCols();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
    }

    formParams() {
      const form = this.querySelector('[data-facets-form]');
      const params = new URLSearchParams();
      if (form) {
        for (const [name, value] of new FormData(form)) if (value !== '') params.append(name, value);
      }
      const sort = this.querySelector('select[name="sort_by"]');
      if (sort) params.set('sort_by', sort.value);
      return params;
    }

    async apply(params) {
      params.delete('page');
      params.delete('section_id');
      const query = params.toString();
      const url = `${window.location.pathname}?${query}${query ? '&' : ''}section_id=${this.dataset.sectionId}`;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const focusedId = document.activeElement && document.activeElement.id;
        const closed = [...this.querySelectorAll('details.facet:not([open])')].map((details) => details.id);

        doc.querySelectorAll('[data-facets-part]').forEach((fresh) => {
          const current = this.querySelector(`[data-facets-part="${fresh.dataset.facetsPart}"]`);
          if (current) current.innerHTML = fresh.innerHTML;
        });
        closed.forEach((id) => {
          const details = document.getElementById(id);
          if (details) details.open = false;
        });
        if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

        const status = this.querySelector('[data-facets-status]');
        const count = this.querySelector('[data-facets-count]');
        if (status && count) status.textContent = count.textContent.trim();
        this.restoreCols();
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        window.Theme.init(this);
      } catch (error) {
        if (error.name !== 'AbortError') window.location.search = query;
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    setCols(cols, remember) {
      const grid = this.querySelector('[data-grid]');
      if (grid) grid.style.setProperty('--cols', cols);
      this.querySelectorAll('[data-grid-cols]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.gridCols === String(cols)));
      });
      if (!remember) return;
      try {
        localStorage.setItem(COLS_KEY, cols);
      } catch (error) {
        /* storage unavailable */
      }
    }

    restoreCols() {
      let cols = null;
      try {
        cols = localStorage.getItem(COLS_KEY);
      } catch (error) {
        /* storage unavailable */
      }
      if (cols) this.setCols(cols, false);
    }
  }

  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.status = this.querySelector('[data-load-status]');
      this.button.hidden = false;
      this.closest('.pagination')?.classList.add('is-enhanced');
      this.onClick = () => this.load();
      this.button.addEventListener('click', this.onClick);
    }

    async load() {
      const grid = document.getElementById(this.dataset.grid);
      if (!grid) return;
      const url = new URL(this.dataset.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      this.button.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(String(response.status));
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const items = [...doc.querySelectorAll(`#${CSS.escape(this.dataset.grid)} > li`)];
        grid.append(...items);
        window.Theme.init(grid);
        // Move focus to the first new product so keyboard users continue where the list grew.
        const firstLink = items[0] && items[0].querySelector('a[href]');
        if (firstLink) firstLink.focus();

        const next = doc.querySelector('load-more');
        if (next) {
          this.dataset.nextUrl = next.dataset.nextUrl;
          this.status.textContent = next.querySelector('[data-load-status]').textContent;
        } else {
          this.status.textContent = this.dataset.allText;
          this.button.remove();
        }
      } catch (error) {
        window.location.href = this.dataset.nextUrl;
      } finally {
        if (this.button.isConnected) this.button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('facet-filters', FacetFilters);
  customElements.define('load-more', LoadMore);
})();
